/**
 * FV見出しを「心臓の鼓動」で出現させる。
 * 拡張期休止(ぼんやり) → 収縮期の鋭いアタック → 微オーバーシュート → 指数関数的にゆるく定常、
 * を opacity と blur の時系列で表現（Web Animations API）。
 * 参照: addict_intraction / src/demos/text-pulse-cardio.astro
 * 対象: [data-pulse-cardio]（FV即時）/ [data-pulse-cardio-inview]（見出しinview／全て<main>内）/ [data-burst-inview]（footerロゴ＝main外）
 *
 * swup対応: main内の心拍（FV/見出し）は毎ページ mountFvPulseInScope(scope) で仕込む。
 * footerロゴのバースト（main外・永続）は mountFvPulseFooter() で初回1回だけ仕込む（毎遷移で再バーストさせない）。
 */

import type { PageModule } from "./page-module"

/** 1周の総尺(ms)：0.1(休止) + 0.08(アタック) + 0.08(オーバーシュート) + 2.0(定常) ≒ 2.26s */
const CARDIO_DURATION = 2260
/** 再生開始までの遅延(ms)。この間は素の状態(拡張期＝ぼんやり)で待機 */
const START_DELAY = 600
/** 複数要素の再生ずらし(ms) */
const STAGGER = 150

/**
 * カーディオのキーフレーム。
 * 各キーフレームの easing は「そのキーフレーム→次のキーフレーム」区間に適用される。
 * power4.out / sine.inOut / expo.out を cubic-bezier で近似。
 */
const CARDIO_KEYFRAMES: Keyframe[] = [
  { opacity: 0.5, filter: "blur(10px)", offset: 0, easing: "linear" },
  // 0.1s の拡張期休止（同値でホールド）→ 収縮期の鋭いアタック(power4.out)
  { opacity: 0.5, filter: "blur(10px)", offset: 0.044, easing: "cubic-bezier(0.22, 1, 0.36, 1)" },
  // ピーク → 微オーバーシュート(sine.inOut)
  { opacity: 1, filter: "blur(0px)", offset: 0.08, easing: "cubic-bezier(0.37, 0, 0.63, 1)" },
  // オーバーシュート → 指数関数的にゆるく定常(expo.out)
  { opacity: 0.92, filter: "blur(2px)", offset: 0.115, easing: "cubic-bezier(0.16, 1, 0.3, 1)" },
  { opacity: 1, filter: "blur(0px)", offset: 1 },
]

/**
 * 1要素に鼓動アニメーションを再生する
 * @param {HTMLElement} target - 対象要素
 * @param {number} delay - 開始ディレイ(ms)
 */
const playPulseCardio = (target: HTMLElement, delay: number): void => {
  target.style.willChange = "opacity, filter"
  const anim = target.animate(CARDIO_KEYFRAMES, {
    duration: CARDIO_DURATION,
    delay,
    fill: "both",
  })
  anim.finished
    .then(() => {
      target.style.willChange = ""
    })
    .catch(() => {})
}

/**
 * inview で再生する対象要素を返す。
 * 子に [data-pulse-cardio-child] があれば（.Heading 等のグループ）その子を、
 * 無ければ要素自身（フッターロゴ等の単体）を返す。
 * @param {HTMLElement} el - [data-pulse-cardio-inview] 要素
 * @returns {HTMLElement[]} 再生対象の配列
 */
const cardioItems = (el: HTMLElement): HTMLElement[] => {
  const children = Array.from(el.querySelectorAll<HTMLElement>("[data-pulse-cardio-child]"))
  return children.length ? children : [el]
}

/**
 * 発火前に初期状態（キーフレーム先頭＝ぼんやり）を当ててちらつきを防ぐ
 * @param {HTMLElement} el - 対象要素
 */
const preDimCardio = (el: HTMLElement): void => {
  el.style.opacity = "0.5"
  el.style.filter = "blur(10px)"
}

/**
 * グループ（子を stagger）／単体を心拍再生する。stagger は FVタイトルと同じ 150ms。
 * @param {HTMLElement} el - [data-pulse-cardio-inview] 要素
 */
const playCardioInview = (el: HTMLElement): void => {
  cardioItems(el).forEach((item, i) => {
    item.style.opacity = ""
    item.style.filter = ""
    playPulseCardio(item, i * STAGGER)
  })
}

/** reduced-motion 判定（演出せず素の表示のまま） */
const prefersReduced = (): boolean => window.matchMedia("(prefers-reduced-motion: reduce)").matches

/**
 * <main>内の心拍を毎ページ仕込む（PageModule）。
 * [data-pulse-cardio]＝フォントロード後に即時再生（FVタイトル）／
 * [data-pulse-cardio-inview]＝ビューポート進入時に1回再生（.Heading / .Heading2 等・全て<main>内）。
 * inviewの IntersectionObserver はローカル保持し、destroy で disconnect する。
 * @param {HTMLElement} scope - 現在ページの <main>
 * @returns {(() => void) | void} inview監視を解除する destroy（対象が無ければ void）
 */
export const mountFvPulseInScope: PageModule = (scope) => {
  if (prefersReduced()) return

  // FV: フォント確定後に即時再生（拡張期休止ぶんの START_DELAY を各要素に）
  const fvTargets = Array.from(scope.querySelectorAll<HTMLElement>("[data-pulse-cardio]"))
  if (fvTargets.length) {
    const startFv = (): void => {
      fvTargets.forEach((target, i) => playPulseCardio(target, START_DELAY + i * STAGGER))
    }
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(startFv)
    } else {
      startFv()
    }
  }

  // 見出しinview: ビューポート進入で1回
  const inviewTargets = Array.from(scope.querySelectorAll<HTMLElement>("[data-pulse-cardio-inview]"))
  let io: IntersectionObserver | null = null
  if (inviewTargets.length) {
    // ちらつき防止：発火前に初期状態（ぼんやり）をプリセット
    inviewTargets.forEach((el) => cardioItems(el).forEach(preDimCardio))

    if (!("IntersectionObserver" in window)) {
      inviewTargets.forEach(playCardioInview)
    } else {
      io = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return
            playCardioInview(entry.target as HTMLElement)
            io?.unobserve(entry.target)
          })
        },
        { rootMargin: "0px 0px -10% 0px", threshold: 0.2 }
      )
      inviewTargets.forEach((el) => io?.observe(el))
    }
  }

  return () => {
    io?.disconnect()
  }
}

// =============================================
//  バースト出現（footerロゴ専用）：見えない状態から「ブワッ」と拡大しながら現れる
// =============================================

/** バースト1発の総尺(ms) */
const BURST_DURATION = 900

/**
 * バーストのキーフレーム。opacity 0 の“見えない”状態から一気に立ち上げ、微オーバーシュートして収束。
 * 各キーフレームの easing はそのキーフレーム→次区間に適用。expo.out / ease.out を近似。
 */
const BURST_KEYFRAMES: Keyframe[] = [
  { opacity: 0, filter: "blur(10px)", transform: "scale(0.98)", offset: 0, easing: "cubic-bezier(0.16, 1, 0.3, 1)" },
  // ピークで微オーバーシュート（“ブワッ”はopacity/blur主体。ロゴが大きいのでscaleは控えめに）
  { opacity: 1, filter: "blur(0px)", transform: "scale(1.01)", offset: 0.5, easing: "cubic-bezier(0.33, 1, 0.68, 1)" },
  // 等倍に収束
  { opacity: 1, filter: "blur(0px)", transform: "scale(1)", offset: 1 },
]

/**
 * 発火前に“見えない”初期状態を当ててチラつきを防ぐ（footerは初期ビューポート外なのでフラッシュも見えない）
 * @param {HTMLElement} el - 対象要素
 */
const preHideBurst = (el: HTMLElement): void => {
  el.style.opacity = "0"
  el.style.filter = "blur(10px)"
  el.style.transform = "scale(0.98)"
}

/**
 * 1要素をバースト再生する（インラインのプリセットを外してから animate）
 * @param {HTMLElement} el - 対象要素
 */
const playBurst = (el: HTMLElement): void => {
  el.style.willChange = "opacity, filter, transform"
  el.style.opacity = ""
  el.style.filter = ""
  el.style.transform = ""
  const anim = el.animate(BURST_KEYFRAMES, { duration: BURST_DURATION, fill: "both" })
  anim.finished
    .then(() => {
      el.style.willChange = ""
    })
    .catch(() => {})
}

/** inviewトリガー用オブザーバ（GC回避＋永続のためモジュールスコープで保持） */
let burstInviewObserver: IntersectionObserver | null = null

/**
 * footer(main外・永続)のロゴ演出を初回1回だけ仕込む。
 * footer内の [data-burst-inview]（ロゴ）をビューポート進入で1回「ブワッ」と出現。
 * footer は swup 遷移で差し替わらないので再実行しない＝毎遷移で再バーストしない。
 * ※ [data-pulse-cardio-inview] は全て <main> 内にあり、こちらは扱わない（mountFvPulseInScope 担当）。
 */
export const mountFvPulseFooter = (): void => {
  if (prefersReduced()) return // reduced-motion では演出せず素の表示のまま（invisibleで固着させない）

  const footer = document.querySelector<HTMLElement>(".footer")
  const targets = footer
    ? Array.from(footer.querySelectorAll<HTMLElement>("[data-burst-inview]"))
    : Array.from(document.querySelectorAll<HTMLElement>("[data-burst-inview]")).filter((el) => !el.closest("main"))
  if (!targets.length) return

  // ちらつき防止：発火前に“見えない”初期状態をプリセット
  targets.forEach(preHideBurst)

  // 非対応環境では即再生
  if (!("IntersectionObserver" in window)) {
    targets.forEach(playBurst)
    return
  }

  // モジュールスコープに保持（ローカルconstだと参照喪失でGCされ発火しないため／永続なので解除もしない）
  burstInviewObserver = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return
        playBurst(entry.target as HTMLElement)
        burstInviewObserver?.unobserve(entry.target)
      })
    },
    { rootMargin: "0px 0px -10% 0px", threshold: 0.2 }
  )
  targets.forEach((el) => burstInviewObserver?.observe(el))
}
